import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { FilterContextData } from 'contexts/FilterContext';
import { usePackArchitectureContext } from 'contexts/PackArchitectureContext';
import { usePpaQueryFilters } from 'hooks/ppa/usePpaQueryFilters';
import { formatWord } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PPA_PACK_ARCH_FILTERED_EMPTY_STATE,
    PPA_PACK_ARCH_GLOBAL_FILTER_APPLIED,
} from 'utils/mixpanelEventNames';

import { buildPackArchitectureGlobalFiltersSummary } from './packArchitectureGlobalFilters.helpers';

const PAGE_NAME = 'ppa/pack-architecture';

const getAppliedFilterNames = (allCheckedAttribute?: Record<string, Record<string, unknown>>) =>
    Object.entries(allCheckedAttribute || {})
        .filter(([, selectedValues]) => Object.keys(selectedValues || {}).length > 0)
        .map(([attributeType]) => formatWord(attributeType))
        .sort((left, right) => left.localeCompare(right));

/**
 * Tracks global-filter apply + empty-result events while on Pack Architecture.
 *
 * Fires once per settled filter change (after loading completes) — not on intermediate updates.
 * Deduplication strategy:
 * - Apply events: Track changes to human-readable filter summary (not just query key)
 * - Empty events: Use composite key of (queryKey, showNoFilterSelection, hasChartData)
 *
 * Note: `trackEventMixpanel` always posts to 2 Mixpanel projects, so Network may show
 * 2 identical `send_user_*` rows per logical event. That is expected.
 */
export const usePackArchitectureGlobalFilterTracking = () => {
    const {
        allCheckedAttribute,
        timeSelectionLabel,
        timePeriodList,
    } = useContext(FilterContextData);
    const { chartRequest, hasChartData, isChartPanelLoading, showNoFilterSelection } =
        usePackArchitectureContext();
    const { globalFilterQueryKey, filtersLoading } = usePpaQueryFilters();

    const filterState = useMemo(
        () =>
            buildPackArchitectureGlobalFiltersSummary({
                timeSelectionLabel,
                chartRequest,
                timePeriodList,
                allCheckedAttribute,
            }),
        [allCheckedAttribute, chartRequest, timePeriodList, timeSelectionLabel],
    );

    const trackingStateRef = useRef({
        filterApply: {
            hasBaseline: false,
            previousState: '-',
            previousKey: '',
            lastTrackedKey: '',
        },
        emptyState: {
            isPending: false,
            lastKey: '',
        },
    });

    const initializeApplyBaseline = useCallback(() => {
        trackingStateRef.current.filterApply.previousState = filterState;
        trackingStateRef.current.filterApply.previousKey = globalFilterQueryKey;
    }, [filterState, globalFilterQueryKey]);

    // ── global_filter_applied (once per settled query-key change) ───────────
    useEffect(() => {
        const { filterApply } = trackingStateRef.current;

        if (filtersLoading) return;

        if (!filterApply.hasBaseline) {
            filterApply.hasBaseline = true;
            initializeApplyBaseline();
            return;
        }

        if (!filterApply.previousState && filterState) {
            initializeApplyBaseline();
            return;
        }

        if (globalFilterQueryKey === filterApply.previousKey) return;

        const previousFilterState = filterApply.previousState;
        const selectedFilterState = filterState || '-';

        if (selectedFilterState === previousFilterState) {
            filterApply.previousKey = globalFilterQueryKey;
            return;
        }

        if (filterApply.lastTrackedKey === globalFilterQueryKey) {
            filterApply.previousKey = globalFilterQueryKey;
            filterApply.previousState = selectedFilterState;
            return;
        }
        filterApply.lastTrackedKey = globalFilterQueryKey;

        trackEventMixpanel(PPA_PACK_ARCH_GLOBAL_FILTER_APPLIED, {
            page_name: PAGE_NAME,
            previous_filter_state: previousFilterState,
            selected_filter_state: selectedFilterState,
        });

        filterApply.previousState = selectedFilterState;
        filterApply.previousKey = globalFilterQueryKey;
        trackingStateRef.current.emptyState.isPending = true;
        trackingStateRef.current.emptyState.lastKey = '';
    }, [filterState, filtersLoading, globalFilterQueryKey, initializeApplyBaseline]);

    // ── filtered_empty_state (after an apply settles with no data) ──────────
    useEffect(() => {
        const { emptyState } = trackingStateRef.current;

        if (!emptyState.isPending) return;
        if (filtersLoading || isChartPanelLoading) return;

        const isEmpty = showNoFilterSelection || !hasChartData;
        if (!isEmpty) {
            emptyState.isPending = false;
            return;
        }

        // Use JSON.stringify for robust composite key to avoid ambiguous delimiters
        const emptyKey = JSON.stringify({ globalFilterQueryKey, showNoFilterSelection, hasChartData });
        if (emptyKey === emptyState.lastKey) return;
        emptyState.lastKey = emptyKey;
        emptyState.isPending = false;

        const filterNames = getAppliedFilterNames(allCheckedAttribute);
        // Prepend 'Time Period' first for consistent analytics ordering
        if (timeSelectionLabel?.trim()) {
            filterNames.unshift('Time Period');
        }

        trackEventMixpanel(PPA_PACK_ARCH_FILTERED_EMPTY_STATE, {
            page_name: PAGE_NAME,
            filter_names: filterNames,
            filter_count: filterNames.length,
        });
    }, [globalFilterQueryKey, timeSelectionLabel, filtersLoading, isChartPanelLoading, showNoFilterSelection, hasChartData, allCheckedAttribute]);
};
